/**
 * Tests from business documents (BRD, user stories, specs) for web and mobile projects.
 *
 * Workflow (one RequirementSet per upload, kept so the review can be resumed and saved tests stay traceable):
 *   1. Documents: PDF / Word (.docx) / text / Markdown files or pasted text -> plain text (API, never sent anywhere else).
 *   2. Requirements: the project's AI model lists testable requirements (REQ-1…, acceptance criteria, priority,
 *      where in the document) and open questions; people edit, add, remove and pick which to cover.
 *   3. Test cases: for the picked requirements the model drafts tests (positive, negative, edge) in 1QA's own
 *      steps (open / act / assert / wait) using the project's test data names; people edit, accept or reject.
 *   4. Save: accepted drafts become ordinary tests (tagged, linked to the set + requirement keys), optionally a group.
 *
 * The model's replies are parsed here (pure, tolerant of code fences / extra prose), so API and tests agree.
 */
import { z } from 'zod';
import { STEP_KINDS, type Platform, type TestStep } from './testing.js';

export const REQUIREMENT_LIMITS = {
  files: 5,
  /** Bytes per uploaded file. */
  fileBytes: 15 * 1024 * 1024,
  /** Characters of document text kept per set (all files together). */
  chars: 400_000,
  /** Characters sent to the model per call when finding requirements (longer documents go in parts). */
  chunkChars: 60_000,
  requirements: 200,
  /** Requirements per model call when drafting tests. */
  requirementsPerCall: 6,
  drafts: 400,
  /** Tests the model drafts per requirement (at most). */
  draftsPerRequirement: 5,
} as const;

/** File types read on upload. Images inside documents are not read. */
export const REQUIREMENT_FILE_TYPES = ['.pdf', '.docx', '.txt', '.md', '.markdown', '.csv'] as const;
export const REQUIREMENT_FILE_ACCEPT = REQUIREMENT_FILE_TYPES.join(',');

export const REQUIREMENT_PRIORITIES = ['HIGH', 'MEDIUM', 'LOW'] as const;
export type RequirementPriority = (typeof REQUIREMENT_PRIORITIES)[number];

/** What a draft test checks: the main path, wrong input / refused actions, or limits and unusual cases. */
export const DRAFT_KINDS = ['positive', 'negative', 'edge'] as const;
export type DraftKind = (typeof DRAFT_KINDS)[number];
export const DRAFT_KIND_LABEL: Record<DraftKind, string> = { positive: 'Positive', negative: 'Negative', edge: 'Edge case' };

export const DRAFT_STATUSES = ['draft', 'accepted', 'rejected'] as const;
export type DraftStatus = (typeof DRAFT_STATUSES)[number];

export const REQUIREMENT_KEY_RE = /^REQ-\d{1,4}$/;

export const requirementSchema = z.object({
  /** REQ-1, REQ-2… unique within the set. */
  key: z.string().regex(REQUIREMENT_KEY_RE),
  title: z.string().trim().min(1).max(200),
  description: z.string().max(4000).default(''),
  /** Acceptance criteria, one per line item. */
  acceptance: z.array(z.string().trim().min(1).max(1000)).max(30).default([]),
  priority: z.enum(REQUIREMENT_PRIORITIES).default('MEDIUM'),
  /** Where it is in the document (section / heading / short quote). */
  source: z.string().max(500).default(''),
  /** Picked for test generation. */
  selected: z.boolean().default(true),
  /** Added by a person rather than found by the model. */
  manual: z.boolean().default(false),
});
export type Requirement = z.infer<typeof requirementSchema>;

const draftStepSchema = z.object({
  id: z.string().min(1).max(64),
  kind: z.enum(STEP_KINDS),
  value: z.string().max(2000),
});

export const testDraftSchema = z.object({
  id: z.string().min(1).max(64),
  name: z.string().trim().min(1).max(200),
  requirementKeys: z.array(z.string().regex(REQUIREMENT_KEY_RE)).min(1).max(20),
  kind: z.enum(DRAFT_KINDS).default('positive'),
  priority: z.enum(REQUIREMENT_PRIORITIES).default('MEDIUM'),
  /** What the test proves and what it needs before it starts (accounts, data). */
  description: z.string().max(4000).default(''),
  steps: z.array(draftStepSchema).min(1).max(200),
  status: z.enum(DRAFT_STATUSES).default('draft'),
  /** Set when saved as a test. */
  savedTestId: z.string().nullable().default(null),
});
export type TestDraft = z.infer<typeof testDraftSchema>;

export const requirementSetPatchSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    requirements: z.array(requirementSchema).max(REQUIREMENT_LIMITS.requirements).optional(),
    drafts: z.array(testDraftSchema).max(REQUIREMENT_LIMITS.drafts).optional(),
    questions: z.array(z.string().max(1000)).max(50).optional(),
  })
  .strict();
export type RequirementSetPatch = z.infer<typeof requirementSetPatchSchema>;

export const requirementUploadSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    files: z
      .array(z.object({ name: z.string().trim().min(1).max(255), data: z.string().min(1) }))
      .max(REQUIREMENT_LIMITS.files)
      .default([]),
    /** Pasted text (e.g. user stories from a ticket). */
    text: z.string().max(REQUIREMENT_LIMITS.chars).optional(),
    /** Extra guidance for the model: roles, test accounts, what to leave out. */
    notes: z.string().max(4000).optional(),
  })
  .strict()
  .refine((b) => b.files.length > 0 || (b.text ?? '').trim().length > 0, 'Add a document or paste the requirements text');
export type RequirementUpload = z.infer<typeof requirementUploadSchema>;

export const generateDraftsSchema = z
  .object({
    /** Requirements to draft tests for; omitted = every selected requirement without tests yet. */
    keys: z.array(z.string().regex(REQUIREMENT_KEY_RE)).max(REQUIREMENT_LIMITS.requirements).optional(),
    /** Replace drafts of these requirements that are not saved yet (Regenerate). */
    replace: z.boolean().default(false),
  })
  .strict();

export const saveDraftsSchema = z
  .object({
    draftIds: z.array(z.string().min(1)).min(1).max(REQUIREMENT_LIMITS.drafts),
    /** Also put the saved tests in a new group, in requirement order. */
    groupName: z.string().trim().min(1).max(200).optional(),
  })
  .strict();

export interface RequirementSource {
  name: string;
  /** pdf / docx / text / pasted */
  kind: 'pdf' | 'docx' | 'text' | 'pasted';
  bytes: number;
  chars: number;
  /** Something worth telling (e.g. "page 3 had no text: scanned images are not read"). */
  note?: string;
}

export type RequirementSetStatus = 'NEW' | 'REQUIREMENTS' | 'DRAFTS' | 'SAVED';

export interface RequirementSetSummary {
  id: string;
  projectId: string;
  name: string;
  status: RequirementSetStatus;
  sources: RequirementSource[];
  chars: number;
  counts: { requirements: number; selected: number; drafts: number; accepted: number; saved: number };
  createdBy: { id: string; name: string };
  createdAt: string;
  updatedAt: string;
}

/** The AI model working in the background on a set; the page polls until it is gone (done) or `error` is set. */
export interface RequirementJob {
  kind: 'requirements' | 'drafts';
  /** Model calls done / to do (document parts, or requirement batches). */
  done: number;
  total: number;
  startedAt: string;
  error?: string;
}

/** A job that has not moved for this long is treated as lost (the API restarted while it ran). */
export const REQUIREMENT_JOB_STALE_MS = 15 * 60_000;

export function isJobRunning(job: RequirementJob | null | undefined, now = Date.now()): boolean {
  return !!job && !job.error && now - new Date(job.startedAt).getTime() < REQUIREMENT_JOB_STALE_MS;
}

export interface RequirementSetView extends RequirementSetSummary {
  notes: string | null;
  /** First part of the extracted text, so people can check the document was read. */
  preview: string;
  requirements: Requirement[];
  drafts: TestDraft[];
  questions: string[];
  /** The caller may edit it (creator or Project Admin). */
  canEdit: boolean;
  job: RequirementJob | null;
  /** Test data names of the project (key -> secret), for the review: names the drafts use that do not exist yet. */
  dataKeys: Record<string, boolean>;
}

// ---------- parsing the model's replies ----------

/** The first JSON value in a reply (inside ```json fences or bare), or null. */
export function extractJson(text: string): unknown {
  const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const candidates = [fenced?.[1], text];
  for (const c of candidates) {
    if (!c) continue;
    const start = c.search(/[[{]/);
    if (start < 0) continue;
    const open = c[start]!;
    const close = open === '{' ? '}' : ']';
    const end = c.lastIndexOf(close);
    if (end <= start) continue;
    try {
      return JSON.parse(c.slice(start, end + 1));
    } catch {
      /* try the next candidate */
    }
  }
  return null;
}

const str = (v: unknown, max: number) => (typeof v === 'string' ? v.trim().slice(0, max) : '');
const strList = (v: unknown, max: number, maxItems: number) =>
  (Array.isArray(v) ? v : typeof v === 'string' ? [v] : [])
    .map((x) => str(x, max))
    .filter(Boolean)
    .slice(0, maxItems);
const priorityOf = (v: unknown): RequirementPriority => {
  const p = str(v, 20).toUpperCase();
  if (p.startsWith('H') || p === 'MUST' || p === 'CRITICAL') return 'HIGH';
  if (p.startsWith('L') || p === 'COULD' || p === 'WONT') return 'LOW';
  return 'MEDIUM';
};

/** Requirements from a reply `{ requirements: [...], questions: [...] }` (or a bare array); keys are given out later. */
export function parseRequirementsReply(text: string): { requirements: Omit<Requirement, 'key'>[]; questions: string[] } {
  const json = extractJson(text) as { requirements?: unknown; questions?: unknown } | unknown[] | null;
  const list = Array.isArray(json) ? json : Array.isArray(json?.requirements) ? json.requirements : [];
  const requirements: Omit<Requirement, 'key'>[] = [];
  for (const raw of list as Record<string, unknown>[]) {
    if (!raw || typeof raw !== 'object') continue;
    const title = str(raw.title ?? raw.name ?? raw.summary, 200);
    if (!title) continue;
    requirements.push({
      title,
      description: str(raw.description ?? raw.details, 4000),
      acceptance: strList(raw.acceptance ?? raw.acceptanceCriteria ?? raw.criteria, 1000, 30),
      priority: priorityOf(raw.priority),
      source: str(raw.source ?? raw.section ?? raw.reference, 500),
      selected: true,
      manual: false,
    });
  }
  const questions = Array.isArray(json) ? [] : strList(json?.questions, 1000, 50);
  return { requirements, questions };
}

/** Same requirement found twice (overlapping document parts): same title, ignoring case and spaces. */
export function mergeRequirements(parts: Omit<Requirement, 'key'>[][], startAt = 1): Requirement[] {
  const seen = new Set<string>();
  const out: Requirement[] = [];
  let n = startAt;
  for (const part of parts) {
    for (const r of part) {
      const k = r.title.toLowerCase().replace(/\s+/g, ' ');
      if (seen.has(k)) continue;
      seen.add(k);
      if (out.length >= REQUIREMENT_LIMITS.requirements) return out;
      out.push({ ...r, key: `REQ-${n++}` });
    }
  }
  return out;
}

/** The next free REQ-n. */
export function nextRequirementKey(requirements: Pick<Requirement, 'key'>[]): string {
  const max = requirements.reduce((m, r) => Math.max(m, Number(r.key.slice(4)) || 0), 0);
  return `REQ-${max + 1}`;
}

const stepKindOf = (v: unknown): TestStep['kind'] | null => {
  const k = str(v, 20).toLowerCase();
  if (k === 'open' || k === 'navigate' || k === 'launch' || k === 'goto') return 'open';
  if (k === 'act' || k === 'action' || k === 'do' || k === 'click' || k === 'type') return 'act';
  if (k === 'assert' || k === 'check' || k === 'verify' || k === 'expect') return 'assert';
  if (k === 'wait') return 'wait';
  return null;
};

/**
 * Draft tests from a reply `{ tests: [...] }` (or a bare array). Keeps only keys the set has; mobile tests start with
 * one `open` (launches the app) and web tests with an `open` too (the app address when the model gave none).
 */
export function parseDraftsReply(
  text: string,
  opts: { platform: Platform; knownKeys: Set<string>; newId: () => string },
): TestDraft[] {
  const json = extractJson(text) as { tests?: unknown } | unknown[] | null;
  const list = Array.isArray(json) ? json : Array.isArray(json?.tests) ? json.tests : [];
  const out: TestDraft[] = [];
  for (const raw of list as Record<string, unknown>[]) {
    if (!raw || typeof raw !== 'object') continue;
    const name = str(raw.name ?? raw.title, 200);
    const keys = strList(raw.requirementKeys ?? raw.requirements ?? raw.requirement, 20, 20)
      .map((k) => k.toUpperCase())
      .filter((k) => opts.knownKeys.has(k));
    if (!name || !keys.length) continue;
    const steps: TestStep[] = [];
    for (const s of Array.isArray(raw.steps) ? (raw.steps as Record<string, unknown>[]) : []) {
      if (!s || typeof s !== 'object') continue;
      const kind = stepKindOf(s.kind ?? s.type);
      if (!kind) continue;
      let value = str(s.value ?? s.text ?? s.step, 2000);
      if (kind === 'wait') value = String(Math.min(Math.max(Number.parseInt(value, 10) || 1000, 0), 30_000));
      if (kind === 'open' && opts.platform !== 'WEB') value = '';
      if (kind !== 'open' && kind !== 'wait' && !value) continue;
      steps.push({ id: opts.newId(), kind, value });
    }
    if (!steps.some((s) => s.kind === 'act' || s.kind === 'assert')) continue;
    if (steps[0]?.kind !== 'open') steps.unshift({ id: opts.newId(), kind: 'open', value: opts.platform === 'WEB' ? '/' : '' });
    if (opts.platform !== 'WEB') {
      // One launch at the start is enough on mobile; later "open" steps would relaunch the app.
      for (let i = steps.length - 1; i > 0; i--) if (steps[i]!.kind === 'open') steps.splice(i, 1);
    }
    const kind = str(raw.kind ?? raw.category, 20).toLowerCase();
    out.push({
      id: opts.newId(),
      name,
      requirementKeys: [...new Set(keys)],
      kind: kind.startsWith('neg') ? 'negative' : kind.startsWith('edge') || kind.startsWith('bound') ? 'edge' : 'positive',
      priority: priorityOf(raw.priority),
      description: str(raw.description ?? raw.purpose, 4000),
      steps: steps.slice(0, 200),
      status: 'draft',
      savedTestId: null,
    });
  }
  return out;
}

/** Per requirement: how many drafts cover it and how many are accepted or saved. */
export function coverageOf(requirements: Requirement[], drafts: TestDraft[]): Map<string, { drafts: number; accepted: number; saved: number }> {
  const out = new Map(requirements.map((r) => [r.key, { drafts: 0, accepted: 0, saved: 0 }]));
  for (const d of drafts) {
    if (d.status === 'rejected') continue;
    for (const k of d.requirementKeys) {
      const c = out.get(k);
      if (!c) continue;
      c.drafts++;
      if (d.status === 'accepted' || d.savedTestId) c.accepted++;
      if (d.savedTestId) c.saved++;
    }
  }
  return out;
}

/** Splits long text into parts of about `size` characters at paragraph breaks, with a little overlap. */
export function chunkText(text: string, size: number = REQUIREMENT_LIMITS.chunkChars, overlap = 1500): string[] {
  if (text.length <= size) return [text];
  const parts: string[] = [];
  let start = 0;
  while (start < text.length) {
    let end = Math.min(start + size, text.length);
    if (end < text.length) {
      const brk = text.lastIndexOf('\n\n', end);
      if (brk > start + size / 2) end = brk;
    }
    parts.push(text.slice(start, end));
    if (end >= text.length) break;
    start = Math.max(end - overlap, start + 1);
  }
  return parts;
}

/** Test names unique per project: "Name", "Name (2)"… are given out by the API with uniqueTestName. */
export const BRD_TAG = 'from-requirements';
